import { NavLink } from 'react-router-dom';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTheme } from '@/theme/ThemeProvider';
import { useAuth } from '@/providers/AuthProvider';
import { useUserChats, useChatSocket } from '@/services/chat';
import { getChatUserId } from '@/utils/chatUser';
import {
  requestNotificationPermission,
  showNotification,
  NotificationPermissionState,
} from '@/utils/notifications';
import styles from './AppShell.module.css';

type NavItem = {
  to: string;
  label: string;
  shortLabel?: string;
  icon: JSX.Element;
};

const iconProps = {
  role: 'presentation',
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  strokeWidth: 1.8,
  stroke: 'currentColor',
  fill: 'none',
};

const navItems: NavItem[] = [
  {
    to: '/',
    label: 'Discover',
    icon: (
      <svg {...iconProps}>
        <path d="M12 3l2.5 7.5L22 13l-7.5 2.5L12 23l-2.5-7.5L2 13l7.5-2.5z" />
      </svg>
    ),
  },
  {
    to: '/chat',
    label: 'Chat',
    icon: (
      <svg {...iconProps}>
        <path d="M5 19l-2 2V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H7z" />
        <path d="M8 10h8M8 7h8" />
      </svg>
    ),
  },
  {
    to: '/share',
    label: 'Share',
    icon: (
      <svg {...iconProps}>
        <path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7" />
        <path d="M12 16V4m0 0l-4 4m4-4 4 4" />
      </svg>
    ),
  },
  {
    to: '/profile',
    label: 'Profile & Settings',
    shortLabel: 'Profile',
    icon: (
      <svg {...iconProps}>
        <circle cx="12" cy="8" r="3.5" />
        <path d="M5 20a7 7 0 0 1 14 0" />
      </svg>
    ),
  },
];

export const AppShell = ({ children }: { children: ReactNode }) => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = useMemo(() => getChatUserId(user), [user]);
  const { data: chatSummary } = useUserChats(userId, { limit: 50 });
  const totalUnread = useMemo(
    () => (chatSummary?.chats ?? []).reduce((sum, chat) => sum + (chat.unreadCount ?? 0), 0),
    [chatSummary?.chats],
  );
  const [latestNotification, setLatestNotification] = useState<{
    senderId: string;
    message: string;
    chatRoomId: string;
    timestamp: number;
  } | null>(null);
  const [notificationPermission, setNotificationPermission] =
    useState<NotificationPermissionState>('default');
  const hasRequestedPermissionRef = useRef(false);

  useChatSocket({
    userId,
    onNotification: (notification) => {
      if (!userId || notification.senderId === userId) {
        return;
      }
      setLatestNotification(notification);
      queryClient.invalidateQueries({ queryKey: ['chat', 'summary'] });

      const maybeNotify = async () => {
        if (notificationPermission === 'denied') {
          return;
        }
        let permission: NotificationPermissionState = notificationPermission;
        if (permission === 'default') {
          permission = await requestNotificationPermission();
          setNotificationPermission(permission);
        }
        if (permission !== 'granted') {
          return;
        }
        await showNotification('New chat message', {
          body: notification.message,
          tag: `chat-${notification.chatRoomId}`,
          data: { chatRoomId: notification.chatRoomId },
        });
      };

      maybeNotify().catch((error) => {
        console.warn('Unable to show chat notification', error);
      });
    },
  });

  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      setNotificationPermission(Notification.permission as NotificationPermissionState);
    }
    if (
      !hasRequestedPermissionRef.current &&
      typeof window !== 'undefined' &&
      'Notification' in window &&
      Notification.permission === 'default'
    ) {
      hasRequestedPermissionRef.current = true;
      requestNotificationPermission()
        .then((permission) => setNotificationPermission(permission))
        .catch(() => setNotificationPermission('denied'));
    }
    if (!latestNotification) {
      return;
    }
    const timeout = setTimeout(() => setLatestNotification(null), 6000);
    return () => clearTimeout(timeout);
  }, [latestNotification]);

  const unreadDisplay = totalUnread > 99 ? '99+' : totalUnread.toString();

  return (
    <div className={styles.appShell}>
      {latestNotification && (
        <div className={styles.toast} role="status" aria-live="assertive">
          <div className={styles.toastBody}>
            <strong>New chat message</strong>
            <span className={styles.toastMeta}>
              {`From ${latestNotification.senderId} · ${new Date(
                latestNotification.timestamp,
              ).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
            </span>
            <p className={styles.toastPreview}>{latestNotification.message}</p>
          </div>
          <button
            type="button"
            className={styles.toastDismiss}
            onClick={() => setLatestNotification(null)}
            aria-label="Dismiss new message notification"
          >
            ×
          </button>
        </div>
      )}
      <header className={styles.header}>
        <div className={styles.brand}>
          <img src={theme.assets.logo} alt="AwkHerd" className={styles.logo} />
          <div>
            <span className={styles.overline}>AwkHerd</span>
            <h1 className={styles.title}>Social Connector</h1>
          </div>
        </div>
        <nav className={styles.nav} aria-label="Primary navigation">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                isActive ? `${styles.navLink} ${styles.navLinkActive}` : styles.navLink
              }
            >
              <span className={styles.navIcon} aria-hidden>
                {item.icon}
              </span>
              <span className={styles.navLabel}>{item.label}</span>
              <span className={styles.navLabelShort}>{item.shortLabel ?? item.label}</span>
              {item.to === '/chat' && totalUnread > 0 && (
                <span
                  className={styles.navBadge}
                  aria-label={`${unreadDisplay} unread chat ${totalUnread === 1 ? 'message' : 'messages'}`}
                >
                  {unreadDisplay}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className={styles.main}>{children}</main>
    </div>
  );
};
